import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HttpService } from '@core/services';
import { LeasingHttpService } from '@core/services/http';
import { LeasingActions } from '@store/actions';
import { LeasingState } from '@store/reducers/leasing.reducer';
import { LeasingSelectors } from '@store/selectors';

type Tab = keyof LeasingState['tabData'];

type PrefetchTabsMap = Partial<Record<Tab, Tab[]>>;

const assertNever = (value: never): never => {
  throw new Error(`Unexpected tab value for the prefetch: ${value}`);
};

export const useLeasingTabPrefetch = (
  currentTab: Tab,
  projectBundleID: string,
) => {
  const dispatch = useDispatch();

  const summaryLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('summary'),
  );
  const workflowLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('workflow'),
  );
  const leaseAuditLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('leaseAudit'),
  );
  const equipmentLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('equipment'),
  );
  const jurisdictionAuditLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('jurisdictionAudit'),
  );
  const financialLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('financial'),
  );

  useEffect(() => {
    const prefetchDelay = 1000;

    const prefetchMap: PrefetchTabsMap = {
      summary: [
        'workflow',
        'leaseAudit',
        'equipment',
        'jurisdictionAudit',
        'financial',
      ],
      workflow: ['leaseAudit', 'equipment', 'jurisdictionAudit', 'financial'],
      leaseAudit: ['workflow', 'equipment', 'jurisdictionAudit', 'financial'],
      equipment: ['workflow', 'leaseAudit', 'jurisdictionAudit', 'financial'],
      jurisdictionAudit: [
        'summary',
        'workflow',
        'leaseAudit',
        'equipment',
        'financial',
      ],
      financial: [
        'summary',
        'workflow',
        'leaseAudit',
        'equipment',
        'jurisdictionAudit',
      ],
    };

    const loadingStates: Record<Tab, boolean> = {
      summary: summaryLoaded,
      workflow: workflowLoaded,
      leaseAudit: leaseAuditLoaded,
      equipment: equipmentLoaded,
      jurisdictionAudit: jurisdictionAuditLoaded,
      financial: financialLoaded,
    };

    const timer = setTimeout(() => {
      prefetchMap[currentTab]?.forEach(async (tabName: Tab) => {
        if (!loadingStates[tabName]) {
          dispatch(
            LeasingActions.setTabLoading.done({
              tabName,
              isLoading: true,
            }),
          );

          switch (tabName) {
            case 'summary':
              break;

            case 'workflow':
              await HttpService.getHttpRequests(
                LeasingHttpService,
              ).getLeasingWorkflowData(projectBundleID, { silent: true });

              break;

            case 'leaseAudit':
              await HttpService.getHttpRequests(
                LeasingHttpService,
              ).getLeasingLeaseAuditData(projectBundleID, { silent: true });

              break;

            case 'equipment':
              await HttpService.getHttpRequests(
                LeasingHttpService,
              ).getLeasingEquipmentTabData(projectBundleID, { silent: true });

              break;

            case 'jurisdictionAudit':
              await HttpService.getHttpRequests(
                LeasingHttpService,
              ).getLeasingJurisdictionAuditData(projectBundleID, {
                silent: true,
              });

              break;

            case 'financial':
              await HttpService.getHttpRequests(
                LeasingHttpService,
              ).getPermittingFinancialData(projectBundleID, { silent: true });

              break;

            default:
              assertNever(tabName);
          }
        }
      });
    }, prefetchDelay);

    return () => clearTimeout(timer);
  }, [
    currentTab,
    summaryLoaded,
    workflowLoaded,
    leaseAuditLoaded,
    equipmentLoaded,
    jurisdictionAuditLoaded,
    financialLoaded,
  ]);
};
